<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed, inject } from "vue"
import { useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import { useFetchWrapper } from "@core/composables"
import { useAuthStore } from "@core/stores/auth.store"
import type { Services } from "@services"
import { ServiceError } from "@core/exceptions"
import { useConfigStore } from "@core/stores/config.store"
import { track } from "@core/tracking/track"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Bienvenue sur {companyName}",
            subtitle: "Connectez-vous à votre compte",
            email: "E-mail",
            password: "Mot de passe",
            submit: "Se connecter",
            forgetPassword: "Mot de passe oublié ?",
            noAccount: "Vous n'avez pas de compte ?",
            subscribe: "S'inscrire",
            firstLogin: "C’est votre première connexion ?",
            firstLoginLink: "Première connexion",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const emit = defineEmits<{
    (e: "click:login"): void
    (e: "click:password"): void
}>()

// ================================
// COMPOSABLES
// ================================
const router = useRouter()
const authStore = useAuthStore()
const configStore = useConfigStore()
const { $login } = inject<Services>("services")!

// ================================
// STATE
// ================================
/** Saisie email / mot de passe */
const form = ref({ username: "", password: "" })
/** Message d’erreur affiché en cas d’échec de connexion */
const errorMessage = ref("")

/** Wrapper d’appel login avec gestion chargement et erreurs */
const loginFetch = useFetchWrapper(
    async () => {
        errorMessage.value = ""
        const response = await $login(form.value.username, form.value.password)
        if (response.token) {
            authStore.setToken(response.token)
            // Tracking V0 : flag staff Homeys (master password) + nouvelle session
            authStore.setAdminHomeys(response.adminHomeys ?? false)
            authStore.generateNewSession()
            await authStore.fetchUser()
            // Fire `Session Started` une fois la session pleinement établie
            // (token + user + companyId chargés). Pour multi-company, le
            // companyId est celui du dernier login (avant pick) — acceptable V0.
            track({ event: "Session Started", payload: {} })
        }
        if (response.multipleCompany) {
            await router.push({
                path: "/login",
                query: {
                    view: "multi-company",
                    username: form.value.username,
                    user: (response as { user?: { _id?: string } })?.user?._id,
                },
            })
            return
        }
        if (response.token) {
            if (
                authStore.previousRoute === "/login" ||
                !authStore.previousRoute
            ) {
                authStore.previousRoute = ""
            }
            await router.push(authStore.previousRoute || "/")
        }
    },
    (err: Error) => {
        const serviceErr = err as ServiceError
        if (serviceErr.code === "invalid_user_not_validated") {
            router.push({
                path: "/login",
                query: { view: "first-login", username: form.value.username },
            })
            return
        }
        errorMessage.value = serviceErr.message ?? "Erreur de connexion"
    }
)

// ================================
// COMPUTED
// ================================
const getFooterLinks = computed(() => [
    { text: t("noAccount"), label: t("subscribe"), link_to: "/inscription" },
    {
        text: t("firstLogin"),
        label: t("firstLoginLink"),
        link_to: "/login?view=first-login",
    },
])

// ================================
// INTERACTIONS
// ================================
/** Soumet le formulaire de connexion. */
function onSubmit(): void {
    if (!form.value.username || !form.value.password) return
    loginFetch.fetch()
}
</script>

<template>
    <BaseCard class="form-auth-login">
        <template #title>
            <FormAuthHeader
                :title="t('title', { companyName: configStore.getScopeBrand })"
                :subtitle="t('subtitle')"
            />
        </template>
        <template #content>
            <form
                class="flex flex-col gap-6 w-full py-6"
                @submit.prevent="onSubmit"
            >
                <div class="flex flex-col gap-4">
                    <BaseInputWrapper
                        input-id="auth-login-email"
                        :label="t('email')"
                    >
                        <BaseInputText
                            id="auth-login-email"
                            v-model="form.username"
                            type="email"
                            class="w-full"
                            required
                        />
                    </BaseInputWrapper>
                    <div class="flex flex-col gap-2">
                        <BaseInputWrapper
                            input-id="auth-login-password"
                            :label="t('password')"
                        >
                            <BasePassword
                                id="auth-login-password"
                                v-model="form.password"
                                toggle-mask
                                fluid
                                class="w-full"
                                required
                                :feedback="false"
                            >
                            </BasePassword>
                        </BaseInputWrapper>
                        <BaseButton
                            variant="link"
                            @click="emit('click:password')"
                            size="small"
                            class="mr-auto w-fit p-0! pl-1!"
                            :label="t('forgetPassword')"
                        >
                        </BaseButton>
                    </div>
                </div>

                <BaseButton
                    type="submit"
                    :label="t('submit')"
                    :loading="loginFetch.isFetching.value"
                    class="w-full"
                />
                <BaseMessage v-if="errorMessage" severity="error">{{
                    errorMessage
                }}</BaseMessage>
            </form>
            <FooterLogin :links="getFooterLinks" :forget-password="true" />
        </template>
    </BaseCard>
</template>
